import React, { useEffect, useRef, Suspense } from "react";
import { createBrowserHistory } from "history";
import { Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import Sub from "layouts/Sub.jsx";
import AuthLayout from "layouts/Auth.jsx";
import AdminLayout from "layouts/Admin.jsx";
import "assets/scss/paper-dashboard.scss?v=1.1.0";
import { Provider, useSelector } from "react-redux";
import { createStore } from "redux";
import reducer from "../src/reducer/index";
import { connect } from "react-redux";
import { setAlert, intStatus, setLogo, setTheme, setFeatures } from "action/index.js";
import { store, persistor } from "store/index";
// Eager: first-screen pages + layouts + per-render chrome (allowlist)
import Login from "views/pages/Login";
import LoginWithCaptcha from "views/pages/LoginWithCaptcha";
import Dashboard from "views/DashboardNew";
import NepalBranch from "components/PopUps/NepalBranch";
import ChromePopUp from "components/PopUps/Chrome";
import LaLoader from "components/Loader/LaLoader.jsx";
import AppDownloadPopup from "components/Frontend/AppDownloadPopup";
import MobileAppBanner from "components/Dashboard/New/MobileAppBanner";
import YouTubeModal from "components/Notification/YoutubeModal";
import { SnackbarProvider, enqueueSnackbar, closeSnackbar } from "notistack";

// Eager: non-component imports (text-file routes, hooks, utils, libs)
import robot from "./components/robots.txt";
// import Sitemap1 from "./components/sitemap.xml";
import fb from "./components/googleaa7a8b2d6a176fd2.txt";
import { isMobile } from "react-device-detect";
import DownloadApp from "components/PopUps/DownloadApp";
import MobileDetect from "mobile-detect";
import { setLoader } from "action";
import { toast } from "react-toastify";
import ScrollToTop from "./hooks/scrollToTop.js";
import routes from "routes";
import useFirebaseNotification from "./hooks/useFirebaseNotification";
import { initGA, trackPageView, initMetaPixel, initTikTok, trackMetaPageView, trackTikTokPageView } from "util/analytics";
import { applyTheme, getMockTheme, mergePartnerTheme } from "utils/whiteLabelTheme";
import { getDefaultFeatures } from "utils/whiteLabelFeatures";
import { isWhiteLabel, getSubdomain } from "utils/whiteLabel";
import API from "api.js";
import "../public/css/new-app.css";

// Always-on white-label landing pages (swapped in for Home/Contact below).
import TwentyTaskHome from "./page/TwentyTaskHome";
import TwentyTaskContact from "./page/TwentyTaskContact";
import PteMaxRouteGuard from "./components/PteMaxRouteGuard";

const NotFound = React.lazy(() => import("./page/NotFound"));

// White-label build detection — single source of truth. utils/whiteLabel
// #isWhiteLabel reads `import.meta.env.VITE_WHITE_LABEL`. Under a white-label
// build every marketing page below is nulled out (and filtered from the router
// at render time), so it never mounts; Home/Contact swap to the TwentyTask pages.
const __WHITE_LABEL_BUILD__ = isWhiteLabel();

// PTEMax route prefix: root in white-label builds, /ptemax otherwise.
// White-label builds own the entire domain, so public marketing pages live at /.
const __PTEMAX_PREFIX__ = __WHITE_LABEL_BUILD__ ? "" : "/ptemax";

// Lazy source pages (route-level code-split) + white-label gate. Each `*_Src` is
// the real lazy page; the exported name is null under white-label so the
// front-array `.filter(p => p.component)` drops it — except Home/Contact, which
// swap to the TwentyTask landing pages. Keeping heavy pages lazy (esp. Home) is
// required for the Prerender readiness fix documented below.
const Home_Src = React.lazy(() => import("./page/Home"));
const Home = __WHITE_LABEL_BUILD__ ? TwentyTaskHome : Home_Src;
const VideoCourse_Src = React.lazy(() => import("./page/VideoCourse"));
const VideoCourse = __WHITE_LABEL_BUILD__ ? null : VideoCourse_Src;
const VideoCourseR3_Src = React.lazy(() => import("./page/VideoCourseR3"));
const VideoCourseR3 = __WHITE_LABEL_BUILD__ ? null : VideoCourseR3_Src;
const WhatWeOffer_Src = React.lazy(() => import("./page/WhatWeOffer"));
const WhatWeOffer = __WHITE_LABEL_BUILD__ ? null : WhatWeOffer_Src;
const MockPlan_Src = React.lazy(() => import("./page/MockPlan"));
const MockPlan = __WHITE_LABEL_BUILD__ ? null : MockPlan_Src;
const Contact_Src = React.lazy(() => import("./page/Contact"));
const Contact = __WHITE_LABEL_BUILD__ ? TwentyTaskContact : Contact_Src;
const About_Src = React.lazy(() => import("./page/About"));
const About = __WHITE_LABEL_BUILD__ ? null : About_Src;
const PayOnline_Src = React.lazy(() => import("./page/PayOnline"));
const PayOnline = __WHITE_LABEL_BUILD__ ? null : PayOnline_Src;
const JoinUs_Src = React.lazy(() => import("./page/JoinUs"));
const JoinUs = __WHITE_LABEL_BUILD__ ? null : JoinUs_Src;
const Branches_Src = React.lazy(() => import("./page/Branches"));
const Branches = __WHITE_LABEL_BUILD__ ? null : Branches_Src;
const Franchise_Src = React.lazy(() => import("./page/Franchise"));
const Franchise = __WHITE_LABEL_BUILD__ ? null : Franchise_Src;
const NaatiAppNew_Src = React.lazy(() => import("./page/NaatiAppNew"));
const NaatiAppNew = __WHITE_LABEL_BUILD__ ? null : NaatiAppNew_Src;
const PTEAppNew_Src = React.lazy(() => import("./page/PTEAppNew"));
const PTEAppNew = __WHITE_LABEL_BUILD__ ? null : PTEAppNew_Src;
const FreeMockTest_Src = React.lazy(() => import("./page/FreeMockTest"));
const FreeMockTest = __WHITE_LABEL_BUILD__ ? null : FreeMockTest_Src;
const Naati_Src = React.lazy(() => import("./page/Naati"));
const Naati = __WHITE_LABEL_BUILD__ ? null : Naati_Src;
const Pte_Src = React.lazy(() => import("./page/Pte"));
const Pte = __WHITE_LABEL_BUILD__ ? null : Pte_Src;
const ReadingPte_Src = React.lazy(() => import("./page/ReadingPte"));
const ReadingPte = __WHITE_LABEL_BUILD__ ? null : ReadingPte_Src;
const WritingPte_Src = React.lazy(() => import("./page/WritingPte"));
const WritingPte = __WHITE_LABEL_BUILD__ ? null : WritingPte_Src;
const SpeakingPte_Src = React.lazy(() => import("./page/SpeakingPte"));
const SpeakingPte = __WHITE_LABEL_BUILD__ ? null : SpeakingPte_Src;
const ListeningPte_Src = React.lazy(() => import("./page/ListeningPte"));
const ListeningPte = __WHITE_LABEL_BUILD__ ? null : ListeningPte_Src;
const PteOnlineClasses_Src = React.lazy(() => import("./page/PteOnlineClasses"));
const PteOnlineClasses = __WHITE_LABEL_BUILD__ ? null : PteOnlineClasses_Src;
const PteMockTest_Src = React.lazy(() => import("./page/PteMockTest"));
const PteMockTest = __WHITE_LABEL_BUILD__ ? null : PteMockTest_Src;
const PteStudyMaterial_Src = React.lazy(() => import("./page/PteStudyMaterial"));
const PteStudyMaterial = __WHITE_LABEL_BUILD__ ? null : PteStudyMaterial_Src;
const Term_Src = React.lazy(() => import("./page/Term"));
const Term = __WHITE_LABEL_BUILD__ ? null : Term_Src;
const Feedback_Src = React.lazy(() => import("./page/Feedback"));
const Feedback = __WHITE_LABEL_BUILD__ ? null : Feedback_Src;
const DailyFeedback_Src = React.lazy(() => import("./page/DailyFeedback"));
const DailyFeedback = __WHITE_LABEL_BUILD__ ? null : DailyFeedback_Src;
const Refund_Src = React.lazy(() => import("./page/Refund"));
const Refund = __WHITE_LABEL_BUILD__ ? null : Refund_Src;
const VideoAll_Src = React.lazy(() => import("./page/VideoAll"));
const VideoAll = __WHITE_LABEL_BUILD__ ? null : VideoAll_Src;
const TestimonialAll_Src = React.lazy(() => import("./page/TestimonialAll"));
const TestimonialAll = __WHITE_LABEL_BUILD__ ? null : TestimonialAll_Src;
const ScoreCardAll_Src = React.lazy(() => import("./page/ScoreCardAll"));
const ScoreCardAll = __WHITE_LABEL_BUILD__ ? null : ScoreCardAll_Src;
const PteOnlineCoaching_Src = React.lazy(() => import("./page/PteOnlineCoaching"));
const PteOnlineCoaching = __WHITE_LABEL_BUILD__ ? null : PteOnlineCoaching_Src;
const NaatiOnlineCoaching_Src = React.lazy(() => import("./page/NaatiOnlineCoaching"));
const NaatiOnlineCoaching = __WHITE_LABEL_BUILD__ ? null : NaatiOnlineCoaching_Src;
const PteCoachingSydney_Src = React.lazy(() => import("./page/PteCoachingSydney"));
const PteCoachingSydney = __WHITE_LABEL_BUILD__ ? null : PteCoachingSydney_Src;
const IeltsOnlineCoaching_Src = React.lazy(() => import("./page/IeltsOnlineCoaching"));
const IeltsOnlineCoaching = __WHITE_LABEL_BUILD__ ? null : IeltsOnlineCoaching_Src;
const IeltsCoachingParramatta_Src = React.lazy(() => import("./page/IeltsCoachingParramatta"));
const IeltsCoachingParramatta = __WHITE_LABEL_BUILD__ ? null : IeltsCoachingParramatta_Src;
const IeltsCoachingSydney_Src = React.lazy(() => import("./page/IeltsCoachingSydney"));
const IeltsCoachingSydney = __WHITE_LABEL_BUILD__ ? null : IeltsCoachingSydney_Src;
const NaatiCoachingSydney_Src = React.lazy(() => import("./page/NaatiCoachingSydney"));
const NaatiCoachingSydney = __WHITE_LABEL_BUILD__ ? null : NaatiCoachingSydney_Src;
const Sitemap_Src = React.lazy(() => import("./page/Sitemap"));
const Sitemap = __WHITE_LABEL_BUILD__ ? null : Sitemap_Src;
const ThankYou_Src = React.lazy(() => import("./page/ThankYou"));
const ThankYou = __WHITE_LABEL_BUILD__ ? null : ThankYou_Src;
const ThankYouPayout_Src = React.lazy(() => import("./page/ThankYouPayout"));
const ThankYouPayout = __WHITE_LABEL_BUILD__ ? null : ThankYouPayout_Src;
const ThankYouRegister_Src = React.lazy(() => import("./page/ThankYouRegister"));
const ThankYouRegister = __WHITE_LABEL_BUILD__ ? null : ThankYouRegister_Src;
const Maintenance_Src = React.lazy(() => import("./page/Maintenance"));
const Maintenance = __WHITE_LABEL_BUILD__ ? null : Maintenance_Src;
const PteCore_Src = React.lazy(() => import("./page/PteCore"));
const PteCore = __WHITE_LABEL_BUILD__ ? null : PteCore_Src;
const LeadThanks_Src = React.lazy(() => import("components/ThankYou/ThankYou"));
const LeadThanks = __WHITE_LABEL_BUILD__ ? null : LeadThanks_Src;
const PrivacyPolicy_Src = React.lazy(() => import("page/PrivacyPolicy"));
const PrivacyPolicy = __WHITE_LABEL_BUILD__ ? null : PrivacyPolicy_Src;
const Inst_Src = React.lazy(() => import("page/Inst"));
const Inst = __WHITE_LABEL_BUILD__ ? null : Inst_Src;
const BusinessPage_Src = React.lazy(() => import("page/BusinessPage"));
const BusinessPage = __WHITE_LABEL_BUILD__ ? null : BusinessPage_Src;
const RedirectToHome_Src = React.lazy(() => import("./page/RedirectToHome"));
const RedirectToHome = __WHITE_LABEL_BUILD__ ? null : RedirectToHome_Src;
const Subscription_Src = React.lazy(() => import("page/Payment"));
const Subscription = __WHITE_LABEL_BUILD__ ? null : Subscription_Src;
const MagicTokenLogin_Src = React.lazy(() => import("page/MagicTokenLogin"));
const MagicTokenLogin = __WHITE_LABEL_BUILD__ ? null : MagicTokenLogin_Src;
const SubSuccess_Src = React.lazy(() => import("page/SubSuccess"));
const SubSuccess = __WHITE_LABEL_BUILD__ ? null : SubSuccess_Src;
const SalesPage_Src = React.lazy(() => import("page/SalesPage"));
const SalesPage = __WHITE_LABEL_BUILD__ ? null : SalesPage_Src;
const AppDownloadRedirect_Src = React.lazy(() => import("./page/AppDownloadRedirect"));
const AppDownloadRedirect = __WHITE_LABEL_BUILD__ ? null : AppDownloadRedirect_Src;
const InvalidRegion_Src = React.lazy(() => import("./page/InvalidRegion"));
const InvalidRegion = __WHITE_LABEL_BUILD__ ? null : InvalidRegion_Src;
const SuccessCard_Src = React.lazy(() => import("components/Payments/SuccessCard"));
const SuccessCard = __WHITE_LABEL_BUILD__ ? null : SuccessCard_Src;
const InvalidRegionAccess_Src = React.lazy(() => import("page/InvalidRegionAccess"));
const InvalidRegionAccess = __WHITE_LABEL_BUILD__ ? null : InvalidRegionAccess_Src;
const Victoria_Src = React.lazy(() => import("components/Frontend/CityPages/Victoria"));
const Victoria = __WHITE_LABEL_BUILD__ ? null : Victoria_Src;
const Melbourne_Src = React.lazy(() => import("components/Frontend/CityPages/Melbourne"));
const Melbourne = __WHITE_LABEL_BUILD__ ? null : Melbourne_Src;
const Queensland_Src = React.lazy(() => import("components/Frontend/CityPages/Queensland"));
const Queensland = __WHITE_LABEL_BUILD__ ? null : Queensland_Src;
const Adelaide_Src = React.lazy(() => import("components/Frontend/CityPages/Adelaide"));
const Adelaide = __WHITE_LABEL_BUILD__ ? null : Adelaide_Src;
const Perth_Src = React.lazy(() => import("components/Frontend/CityPages/Perth"));
const Perth = __WHITE_LABEL_BUILD__ ? null : Perth_Src;
const NSW_Src = React.lazy(() => import("components/Frontend/CityPages/NSW"));
const NSW = __WHITE_LABEL_BUILD__ ? null : NSW_Src;

const hist = createBrowserHistory();

const mapStateToProps = (state) => {
  // console.log("The state : ",!!state.token,!!state.user);
  return {
    token: state.token,
    loaded: state.loaded,
    user: state.user,
    subscribed: state.subscribed,
    alert: state.alert,
  };
};

import { cleanupAllTours } from "components/WebsiteTour/tourData";

// New marketing / SEO pages (lazy) + white-label gate. City pages are declared
// (also gated) in the main declarations block above.
import PteScoreAcademic_Src from "components/Frontend/PteScoreAcademic";
import PteScoreCore_Src from "components/Frontend/PteScoreCore";
const PteScoreAcademic = __WHITE_LABEL_BUILD__ ? null : PteScoreAcademic_Src;
const PteScoreCore = __WHITE_LABEL_BUILD__ ? null : PteScoreCore_Src;

const FameWallPte_Src = React.lazy(() => import("components/Frontend/FameWallPte"));
const FameWallPte = __WHITE_LABEL_BUILD__ ? null : FameWallPte_Src;
const PteScoreSeo_Src = React.lazy(() => import("components/Frontend/PteScoreSeo"));
const PteScoreSeo = __WHITE_LABEL_BUILD__ ? null : PteScoreSeo_Src;
const AIScoreSeo_Src = React.lazy(() => import("components/Frontend/AIScoreSeo"));
const AIScoreSeo = __WHITE_LABEL_BUILD__ ? null : AIScoreSeo_Src;
const PayOnlineCard_Src = React.lazy(() => import("./page/PayOnlineCard"));
const PayOnlineCard = __WHITE_LABEL_BUILD__ ? null : PayOnlineCard_Src;
// Exam voucher stays available under white-label (shared feature, kept on).
const ExamVoucher = React.lazy(() => import("components/Frontend/ExamVoucher"));

// HOC that wraps a PTEMax page with the institute runtime guard.
// PTEMax pages use the OPPOSITE gate from marketing pages:
// - Marketing: __WHITE_LABEL_BUILD__ ? null : Src (hidden from white-label)
// - PTEMax:    __WHITE_LABEL_BUILD__ ? Src : null (shown ONLY in white-label)
// In white-label builds the entire site belongs to the institute, so the guard
// is unnecessary (and would cause redirect loops at root paths).
const withPteMaxGuard = (Component) => {
  if (!Component) return null;
  if (__WHITE_LABEL_BUILD__) return Component; // no guard needed
  const Guarded = () => (
    <PteMaxRouteGuard>
      <Component />
    </PteMaxRouteGuard>
  );
  return Guarded;
};

// PTEMax pages (lazy) — visible only in white-label builds + institute users
const PteMaxHome_Src = React.lazy(() => import("./components/20task/PteMaxHome"));
const PteMaxHome = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxHome_Src : null);
const PteMaxPricing_Src = React.lazy(() => import("./components/20task/PteMaxPricing"));
const PteMaxPricing = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxPricing_Src : null);
const PteMaxFreeMock_Src = React.lazy(() => import("./components/20task/PteMaxFreeMock"));
const PteMaxFreeMock = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxFreeMock_Src : null);
const PteMaxPlatform_Src = React.lazy(() => import("./components/20task/PteMaxPlatform"));
const PteMaxPlatform = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxPlatform_Src : null);
const PteMaxAbout_Src = React.lazy(() => import("./components/20task/PteMaxAbout"));
const PteMaxAbout = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxAbout_Src : null);
const PteMaxInstitutes_Src = React.lazy(() => import("./components/20task/PteMaxInstitutes"));
const PteMaxInstitutes = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxInstitutes_Src : null);
const PteMaxPractice_Src = React.lazy(() => import("./components/20task/PteMaxPractice"));
const PteMaxPractice = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxPractice_Src : null);
const PteMaxExamPattern_Src = React.lazy(() => import("./components/20task/PteMaxExamPattern"));
const PteMaxExamPattern = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxExamPattern_Src : null);
const PteMaxScore_Src = React.lazy(() => import("./components/20task/PteMaxScore"));
const PteMaxScore = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxScore_Src : null);
const PteMaxScoreCalc_Src = React.lazy(() => import("./components/20task/PteMaxScoreCalc"));
const PteMaxScoreCalc = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxScoreCalc_Src : null);
const PteMaxScoreReqs_Src = React.lazy(() => import("./components/20task/PteMaxScoreReqs"));
const PteMaxScoreReqs = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxScoreReqs_Src : null);
const PteMaxIeltsConverter_Src = React.lazy(() => import("./components/20task/PteMaxIeltsConverter"));
const PteMaxIeltsConverter = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxIeltsConverter_Src : null);
const PteMaxExamFees_Src = React.lazy(() => import("./components/20task/PteMaxExamFees"));
const PteMaxExamFees = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxExamFees_Src : null);
const PteMaxVs_Src = React.lazy(() => import("./components/20task/PteMaxVs"));
const PteMaxVs = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxVs_Src : null);
const PteMaxTerms_Src = React.lazy(() => import("./components/20task/PteMaxTerms"));
const PteMaxTerms = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxTerms_Src : null);
const PteMaxPrivacy_Src = React.lazy(() => import("./components/20task/PteMaxPrivacy"));
const PteMaxPrivacy = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxPrivacy_Src : null);
const PteMaxRefund_Src = React.lazy(() => import("./components/20task/PteMaxRefund"));
const PteMaxRefund = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxRefund_Src : null);
const PteMaxLegal_Src = React.lazy(() => import("./components/20task/PteMaxLegal"));
const PteMaxLegal = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxLegal_Src : null);
const PteMaxPracticeTask_Src = React.lazy(() => import("./components/20task/PteMaxPracticeTask"));
const PteMaxPracticeTask = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxPracticeTask_Src : null);
const PteMaxVsDetail_Src = React.lazy(() => import("./components/20task/PteMaxVsDetail"));
const PteMaxVsDetail = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxVsDetail_Src : null);
const PteMaxNotFound_Src = React.lazy(() => import("./components/20task/PteMaxNotFound"));
const PteMaxNotFound = withPteMaxGuard(__WHITE_LABEL_BUILD__ ? PteMaxNotFound_Src : null);

// Marker attribute set on #root once the matched public route's OWN content has
// committed. The Prerender readiness poller (in ReactRouteAll) watches this instead
// of #root.childElementCount, which was too broad — global chrome (MobileAppBanner,
// SnackbarProvider, NepalBranch, YouTubeModal, popups) puts children in #root before
// the actual page renders, so childCount > 0 could flip prerenderReady on an
// incomplete page. This attribute is only set by the route's own lifecycle.
const PRERENDER_READY_ATTR = "data-route-content-ready";

// Diagnostic error boundary. A render-time throw anywhere in a route subtree
// normally unmounts the whole React tree, leaving an empty #root — which is
// exactly the empty snapshot Prerender captures for the Home route while it
// renders fine in every normal browser. This boundary catches such a throw and
// renders the error text INTO the DOM, so the failure becomes visible in
// Prerender's HTML snapshot (readable via curl) instead of being invisible.
// It also guarantees #root has children, so the prerenderReady content-signal
// can still fire. Kept lightweight and safe to leave in production.
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    this.setState({ info });
    // Keep the Prerender readiness signal working even on a route error, so the
    // error snapshot is captured promptly instead of waiting for the cap.
    const root = document.getElementById("root");
    if (root) root.setAttribute(PRERENDER_READY_ATTR, "true");
    // eslint-disable-next-line no-console
    console.error("RouteErrorBoundary caught:", error, info);
  }
  render() {
    if (this.state.error) {
      const e = this.state.error;
      return (
        <div
          data-route-error="true"
          data-route={this.props.routeName || ""}
          style={{ padding: 16, fontFamily: "monospace", whiteSpace: "pre-wrap" }}
        >
          <h1>Route render error{this.props.routeName ? ` @ ${this.props.routeName}` : ""}</h1>
          <p data-error-name>{String(e && e.name)}</p>
          <p data-error-message>{String(e && e.message)}</p>
          <pre data-error-stack>{String((e && e.stack) || "").slice(0, 2000)}</pre>
          <pre data-error-componentstack>
            {String((this.state.info && this.state.info.componentStack) || "").slice(0, 2000)}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// Wraps a matched public route's element. Its effect runs only AFTER that route's
// DOM has committed (a wrapper's effect fires after its children mount), so it marks
// "real page content is on screen" — not merely "something rendered into #root".
const RouteContentMarker = ({ children }) => {
  useEffect(() => {
    const root = document.getElementById("root");
    if (root) root.setAttribute(PRERENDER_READY_ATTR, "true");
    return () => {
      const root = document.getElementById("root");
      if (root) root.removeAttribute(PRERENDER_READY_ATTR);
    };
  }, []);
  return children;
};

const markReady = (element) => <RouteContentMarker>{element}</RouteContentMarker>;

// White-label redirect: strips the /ptemax prefix from legacy URLs and
// redirects to the equivalent root path. E.g. /ptemax/pricing → /pricing,
// /ptemax → /. Used only in white-label builds where PTEMax pages live at /.
const PteMaxLegacyRedirect = () => {
  const loc = useLocation();
  const rest = loc.pathname.replace(/^\/ptemax\/?/, "/");
  const target = rest === "" ? "/" : rest;
  return <Navigate to={target + loc.search + loc.hash} replace />;
};

// White-label default redirect: redirects root path to /auth/login when subdomain exists
const WhiteLabelDefaultRedirect = () => {
  const subdomain = getSubdomain();
  if (__WHITE_LABEL_BUILD__ && subdomain) {
    return <Navigate to="/auth/login" replace />;
  }
  return <PteMaxHome />;
};

const ReactRouteAll = ({ token, loaded, user, subscribed, alert }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const prevLocation = useRef(location.pathname);
  const snackbarRef = useRef(null);
  const intStatuscheck = useSelector((state) => state.intStatus);
  const { fcmToken, notification, youtubeModal, setYoutubeModal } = useFirebaseNotification();
  // console.log("The intstatuscheck : ", intStatuscheck);

  useEffect(() => {
    initGA();
    initMetaPixel('197333187452630');
    initTikTok('CG0870RC77U8JD8FUT4G');
    console.log('Analytics initialized');
  }, []);

  // White-label: fetch partner logo + theme + feature config by partner
  // subdomain and persist them to Redux. The state is rehydrated by
  // redux-persist on subsequent loads, so the API call only happens on the
  // very first visit — afterwards we just re-apply the cached theme to the
  // DOM (CSS vars don't survive a refresh, the Redux values do).
  useEffect(() => {
    // Temporary: Clear persisted logo if it's the old default
    const currentState = store.getState();
    if (currentState.logo === '/images/logol.png') {
      console.log('Clearing old persisted logo');
      store.dispatch(setLogo(''));
    }
    
    if (!__WHITE_LABEL_BUILD__) return;
    const subdomain = getSubdomain();
    const fallbackTheme = getMockTheme(subdomain);
    
    // Resolve the partner subdomain from the host (shared with api.js so the
    // logo/theme lookup and every request header use the identical rule). No
    // subdomain means no partner, so we skip the logo/theme API below.
    const cached = store.getState();
    if (!cached.features) store.dispatch(setFeatures(getDefaultFeatures()));
    
    // Only hit the partner API when we actually have a subdomain to look up.
    if (!subdomain) {
      // No subdomain = no partner, apply fallback theme
      applyTheme(fallbackTheme);
      return;
    }
    
    const bodyFormData = new FormData();
    bodyFormData.set("subdomain", subdomain);
    
    API({
      method: "POST",
      url: "get-logo",
      data: bodyFormData,
    })
      .then((response) => {
        console.log('get-logo API response:', response?.data);
        const apiData = response?.data?.response;
        const logoPath = apiData?.data;
        const themeData = apiData?.theme;
        
        console.log('Extracted logoPath:', logoPath);
        
        // Set logo if available
        if (logoPath) {
          const partnerHost = "https://la-20task-stagingbe.languageacademy.com.au";
          const fullLogoUrl = `${partnerHost}${logoPath}`;
          console.log('Setting logo to:', fullLogoUrl);
          store.dispatch(setLogo(fullLogoUrl));
        } else {
          console.log('No logoPath, not setting logo');
        }
        
        // Use API theme if available, otherwise fallback
        if (themeData && Object.keys(themeData).length > 0) {
          const mergedTheme = mergePartnerTheme(themeData, subdomain);
          store.dispatch(setTheme(mergedTheme));
          applyTheme(mergedTheme);
        } else {
          // API succeeded but no theme data, use fallback
          store.dispatch(setTheme(fallbackTheme));
          applyTheme(fallbackTheme);
        }
      })
      .catch((error) => {
        console.log('get-logo API error:', error);
        // Show toast with API error
        toast.error(`${error?.response?.data?.message || error?.message || 'Unknown error'}`);
        // API failed, use fallback theme
        store.dispatch(setTheme(fallbackTheme));
        applyTheme(fallbackTheme);
        // Redirect to main domain (remove subdomain) when get-logo API fails
        const subdomain = getSubdomain();
        if (subdomain) {
          // Has subdomain, redirect to main domain after short delay to show toast
          setTimeout(() => {
            const hostname = window.location.hostname;
            const port = window.location.port ? `:${window.location.port}` : '';
            const protocol = window.location.protocol;
            const pathname = window.location.pathname;
            
            // Remove subdomain from hostname
            // sss.localhost -> localhost
            // mukeshpte.pteportal.com -> pteportal.com
            const parts = hostname.split('.');
            parts.shift(); // Remove first part (subdomain)
            const mainDomain = parts.join('.');
            
            // Build clean URL without double slashes
            let newUrl = `${protocol}//${mainDomain}${port}`;
            if (pathname && pathname !== '/') {
              newUrl += pathname;
            } else {
              newUrl += '/';
            }
            console.log('Redirecting from:', window.location.href, 'to:', newUrl);
            window.location.href = newUrl;
          }, 2000); // 2 second delay to show toast
        }
      });
  }, []);

  // Prerender.io readiness signal.
  // index.html sets window.prerenderReady = false up-front so the Prerender bot
  // waits for our explicit signal instead of guessing via network-idle. Mark ready
  // once the matched ROUTE'S OWN content has committed — detected via the
  // PRERENDER_READY_ATTR marker set by <RouteContentMarker> (and by the error
  // boundary). We deliberately do NOT use #root.childElementCount: global chrome
  // (banners, snackbars, modals, popups) lands children in #root before the page
  // renders, so childCount > 0 could flip the signal on an incomplete page.
  //
  // Two things we must NOT do, learned the hard way on the heavy Home route
  // (7.4 MB bundle, ~149 images), which takes many seconds to first paint inside
  // Prerender's constrained headless Chrome:
  //   1. Do NOT gate on document.readyState === "complete" — with that many images
  //      a heavy page can sit at "interactive" indefinitely, so the content signal
  //      never fires and we fall through to the cap.
  //   2. Do NOT use a short cap. A premature cap flips prerenderReady=true before
  //      the page has rendered, so the bot snapshots the empty shell — and a cap
  //      shorter than Prerender's own page timeout actively WASTES that budget.
  //      Prerender is configured with a 60s render timeout, so the cap sits just
  //      under that 60s ceiling and only matters as a last-resort fallback for a
  //      genuinely broken route. Real users never hit the cap.
  // Poll with setInterval (NOT requestAnimationFrame — rAF is throttled/suspended in
  // headless/background tabs and may never fire under Prerender). Reset on every
  // navigation so client-side route changes are handled the same way.
  useEffect(() => {
    window.prerenderReady = false;
    // NOTE: do NOT remove PRERENDER_READY_ATTR here. On initial mount React fires
    // child effects (RouteContentMarker, which SETS the attr) BEFORE this parent
    // effect, so clearing it here would clobber the just-set marker and the content
    // signal would never fire (only the 55s cap would). The marker owns the
    // attribute's full lifecycle — its cleanup clears it on unmount/navigation, and
    // window.prerenderReady=false above is the per-navigation reset.
    let done = false;
    const READY_CAP_MS = 55000; // just under Prerender's 60s page timeout; content signal normally wins first
    const POLL_MS = 100;

    const contentIsReady = () => {
      const el = document.getElementById("root");
      return !!el && el.getAttribute(PRERENDER_READY_ATTR) === "true";
    };

    const finish = (fromCap) => {
      if (done) return;
      // CRITICAL: the cap must NEVER declare readiness on an empty #root.
      // On Prerender's CPU-constrained workers the heavy Home route's first
      // paint can cross READY_CAP_MS; if the cap then flipped prerenderReady=true
      // unconditionally, Prerender would snapshot the still-empty shell and cache
      // a blank page (the exact Home-only failure we diagnosed). So when the cap
      // fires before content has committed, do nothing and let the poller keep
      // waiting — Prerender then uses its full page-timeout budget (and won't
      // cache a false "successful" empty render) instead of capturing blank.
      if (fromCap && !contentIsReady()) return;
      done = true;
      clearInterval(intervalId);
      clearTimeout(capId);
      window.prerenderReady = true;
    };

    const intervalId = setInterval(() => {
      if (contentIsReady()) finish(false);
    }, POLL_MS);
    const capId = setTimeout(() => finish(true), READY_CAP_MS);

    return () => {
      done = true;
      clearInterval(intervalId);
      clearTimeout(capId);
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (prevLocation.current !== location.pathname) {
      cleanupAllTours();
      prevLocation.current = location.pathname;
    }
    // GTM is now loaded directly in index.html with route protection
    // No need to call initGTM here anymore

    // Track page views on public routes
    trackPageView(location.pathname + location.search);
    trackMetaPageView();
    trackTikTokPageView();
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'pageview',
        page: location.pathname + location.search,
      });
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (fcmToken) {
      // console.log('FCM Token:', fcmToken);
    }
  }, [fcmToken]);

  useEffect(() => {
    if (notification) {
      // console.log('New notification:', notification);
    }
  }, [notification]);


  const notify = (type, message) => {
    const options = {
      position: "top-right",
      autoClose: 7000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    switch (type) {
      case 1:
        toast.success(message, options);
        break;
      case 2:
        toast.error(message, options);
        break;
      default:
        toast.info(message, options);
        break;
    }
  };

  useEffect(() => {
    if (!loaded) return;

    store.dispatch(setLoader(true));
    store.dispatch(intStatus(navigator.onLine));

    const handleOnline = () => {
      if (snackbarRef.current) {
        closeSnackbar(snackbarRef.current);
        snackbarRef.current = null;
      }

      store.dispatch(intStatus(true));

      const el = document.getElementById("internet-down");
      if (el) el.remove();
    };

    const handleOffline = () => {
      if (snackbarRef.current) return;

      snackbarRef.current = "pending";

      snackbarRef.current = enqueueSnackbar("No internet connection", {
        variant: "error",
        persist: true,
      });

      store.dispatch(intStatus(false));

      if (!document.getElementById("internet-down")) {
        const div = document.createElement("div");
        div.className = "modal-backdrop fade show";
        div.id = "internet-down";

        const parent =
          document.querySelector(".wrapper") ||
          document.querySelector(".dashboardPage") ||
          document.getElementById("root");

        parent.appendChild(div);
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [loaded]);

  // useEffect(() => {
  //   if (location?.pathname) {
  //     isPortalUrl(location.pathname);
  //     isDashboard(location.pathname);
  //   }
  // }, [location?.pathname]);

  useEffect(() => {
    if (alert.flag) {
      notify(alert.type, alert.message);
      store.dispatch(setAlert({ flag: false }));
    }
  }, [alert]);

  useEffect(() => { }, [token]);

  // respond.io chat widget: hide on internal (/admin) routes during SPA navigation.
  // The growth-tool injects <respond-io-widget> as a sibling of <body> (not inside
  // #root), so we must remove that custom element — removing only the script tag
  // leaves the launcher visible until a full reload.
  useEffect(() => {
    const WIDGET_ID = "respondio__growth_tool";
    const WIDGET_SRC =
      "https://cdn.respond.io/widget/widget.js?wId=cbb5ce71-52bf-42ba-9631-c3f70adb0a1b";
    const path = location.pathname;
    // White-label (institute) sites never get LA's respond.io chat — not on the
    // 20task Home/Contact pages either. Otherwise it's hidden on internal routes.
    const isInternal =
      __WHITE_LABEL_BUILD__ ||
      path === "/admin" ||
      path.startsWith("/admin/") ||
      path === "/sub" ||
      path.startsWith("/sub/");

    const removeWidget = () => {
      document.getElementById(WIDGET_ID)?.remove();
      document
        .querySelectorAll(
          'respond-io-widget, [id^="respondio"], iframe[title="Webchat Widget"], iframe[src*="respond.io"]'
        )
        .forEach((el) => el.remove());
      document.body?.classList.remove("respondio-active");
    };

    if (isInternal) {
      removeWidget();
    } else if (!document.getElementById(WIDGET_ID)) {
      const s = document.createElement("script");
      s.id = WIDGET_ID;
      s.src = WIDGET_SRC;
      document.body.appendChild(s);
    }
  }, [location.pathname]);

  let videoCourseComponent;
  let freeMockTestComponent;
  if (import.meta.env.VITE_SHOW_PAGES === "0") {
    videoCourseComponent = RedirectToHome;
    freeMockTestComponent = RedirectToHome;
  } else {
    videoCourseComponent = RedirectToHome;
    freeMockTestComponent = RedirectToHome;
  }

  // Under white-label every entry except the swapped ones (Home → PteMaxHome,
  // Contact → TwentyTaskContact) has `component: null` and is filtered out at
  // render time below, so the marketing routes simply don't mount.
  const front = [
    { path: "/", component: __WHITE_LABEL_BUILD__ ? WhiteLabelDefaultRedirect : Home },
    { path: "/robots.txt", component: robot },
    { path: "/googleaa7a8b2d6a176fd2.html", component: fb },
    { path: "/video-courses", component: VideoCourse },
    { path: "/video-courses-r3", component: VideoCourseR3 },
    { path: "/ai-in-education", component: WhatWeOffer },
    { path: "/about", component: About },
    { path: "/maintenance", component: Maintenance },
    { path: "/pay-online", component: PayOnline },
    { path: "/pay-online-old", component: PayOnlineCard },
    { path: "/join-us", component: JoinUs },
    { path: "/branches", component: Branches },
    { path: "/franchise", component: Franchise },
    { path: "/naati-app", component: NaatiAppNew },
    { path: "/pte-app", component: PTEAppNew },
    { path: "/mock-test", component: FreeMockTest },
    { path: "/exam-voucher", component: ExamVoucher },
    { path: "/free-mock-test", component: freeMockTestComponent },
    // { path: "/mock-feedback/:id", component: Feedback },
    { path: "/daily-feedback", component: DailyFeedback },
    { path: "/contact", component: Contact },
    { path: "/pte-coaching-classes-parramatta", component: Pte },
    { path: "/naati-coaching-classes-parramatta", component: Naati },
    { path: "/pte-reading-test-practice-material", component: ReadingPte },
    { path: "/pte-speaking-test-practice-material", component: SpeakingPte },
    { path: "/pte-writing-test-practice-material", component: WritingPte },
    { path: "/pte-listening-test-practice-material", component: ListeningPte },
    { path: "/pte-online-classes", component: PteOnlineClasses },
    { path: "/pte-mock-test", component: PteMockTest },
    { path: "/pte-study-material", component: PteStudyMaterial },
    { path: "/term", component: Term },
    { path: "/privacy-policy", component: PrivacyPolicy },
    { path: "/video-all", component: VideoAll },
    { path: "/testimonial-all", component: TestimonialAll },
    { path: "/refund-policies", component: Refund },
    { path: "/scorecard", component: ScoreCardAll },
    { path: "/sitemap", component: Sitemap },
    { path: "/pte-online-coaching", component: PteOnlineCoaching },
    { path: "/naati-ccl-online-coaching", component: NaatiOnlineCoaching },
    { path: "/pte-coaching-sydney", component: PteCoachingSydney },
    { path: "/ielts-online-coaching", component: IeltsOnlineCoaching },
    { path: "/ielts-coaching-parramatta", component: IeltsCoachingParramatta },
    { path: "/ielts-coaching-sydney", component: IeltsCoachingSydney },
    { path: "/naati-ccl-coaching-sydney", component: NaatiCoachingSydney },
    { path: "/thank-you", component: ThankYou },
    { path: "/trial-class-thankyou", component: LeadThanks },
    { path: "/thank-you-payout", component: ThankYouPayout },
    { path: "/register-success", component: ThankYouRegister },
    { path: "/pte-core", component: PteCore },
    { path: "/wall-of-fame", component: FameWallPte },
    { path: "/pte-score-calculator", component: PteScoreSeo },
    { path: "/pte-ai-scoring", component: AIScoreSeo },
    { path: "/inst", component: Inst },
    { path: "/business", component: BusinessPage },
    { path: "/user-login", component: MagicTokenLogin },
    { path: "/pay-success", component: SuccessCard },
    { path: "/sales", component: SalesPage },
    { path: "/invalid/region", component: InvalidRegionAccess },
    { path: "/download-app", component: AppDownloadRedirect },
    { path: "/city/victoria", component: Victoria },
    { path: "/city/melbourne", component: Melbourne },
    { path: "/city/queensland", component: Queensland },
    { path: "/city/adelaide", component: Adelaide },
    { path: "/city/greaterperth", component: Perth },
    { path: "/city/newsouthwales", component: NSW },
    { path: "/pte-score-calculator/academic", component: PteScoreAcademic },
    { path: "/pte-score-calculator/core", component: PteScoreCore },
    // PTEMax public pages: served at root (/) in white-label builds, under
    // /ptemax in standard builds. The prefix is resolved at module level.
    // NOTE: PteMaxHome uses "/" directly in white-label (handled by the Home
    // entry above which swaps to PteMaxHome), so we only add the /ptemax
    // entry for non-white-label builds here.
    ...(__WHITE_LABEL_BUILD__ ? [] : [{ path: "/ptemax", component: PteMaxHome }]),
    { path: `${__PTEMAX_PREFIX__}/pricing`, component: PteMaxPricing },
    { path: `${__PTEMAX_PREFIX__}/free-pte-mock-test`, component: PteMaxFreeMock },
    { path: `${__PTEMAX_PREFIX__}/platform`, component: PteMaxPlatform },
    { path: `${__PTEMAX_PREFIX__}/about`, component: PteMaxAbout },
    { path: `${__PTEMAX_PREFIX__}/institutes`, component: PteMaxInstitutes },
    { path: `${__PTEMAX_PREFIX__}/pte-practice`, component: PteMaxPractice },
    { path: `${__PTEMAX_PREFIX__}/pte-exam-pattern-2026`, component: PteMaxExamPattern },
    { path: `${__PTEMAX_PREFIX__}/pte-score`, component: PteMaxScore },
    { path: `${__PTEMAX_PREFIX__}/pte-score-calculator`, component: PteMaxScoreCalc },
    { path: `${__PTEMAX_PREFIX__}/pte-score-requirements`, component: PteMaxScoreReqs },
    { path: `${__PTEMAX_PREFIX__}/pte-to-ielts-converter`, component: PteMaxIeltsConverter },
    { path: `${__PTEMAX_PREFIX__}/pte-exam-fees`, component: PteMaxExamFees },
    { path: `${__PTEMAX_PREFIX__}/pte-vs`, component: PteMaxVs },
    { path: `${__PTEMAX_PREFIX__}/terms`, component: PteMaxTerms },
    { path: `${__PTEMAX_PREFIX__}/privacy`, component: PteMaxPrivacy },
    { path: `${__PTEMAX_PREFIX__}/refund-policy`, component: PteMaxRefund },
    { path: `${__PTEMAX_PREFIX__}/legal`, component: PteMaxLegal },
    { path: `${__PTEMAX_PREFIX__}/pte-practice/:slug`, component: PteMaxPracticeTask },
    { path: `${__PTEMAX_PREFIX__}/pte-vs/:slug`, component: PteMaxVsDetail },
  ];

  const getAdminRoutes = (routeList = routes) => {
    const result = [];

    routeList.forEach((route) => {
      if (route.collapse && route.views) {
        result.push(...getAdminRoutes(route.views));
      } else if (route.layout === "/admin" && route.path) {
        const childPath = route.path.startsWith("/")
          ? route.path.slice(1)
          : route.path;

        const Component = route.component;

        result.push(
          <Route key={childPath} path={childPath} element={<Component />} />
        );
      }
    });

    return result;
  };

  const getAuthRoutes = (routeList = routes) => {
    const result = [];

    routeList.forEach((route) => {
      if (route.collapse && route.views) {
        result.push(...getAuthRoutes(route.views));
      } else if (route.layout === "/auth" && route.path && route.component) {
        const childPath = route.path.startsWith("/")
          ? route.path.slice(1)
          : route.path;

        const Component = route.component;

        result.push(
          <Route key={childPath} path={childPath} element={<Component />} />
        );
      }
    });

    return result;
  };

  const bannerExcludedPaths = [
    "/admin/mock",
    "/admin/extensive/new-sectional-tests",
    "/admin/fulltest",
    "/admin/sectionalTest",
    "/admin/test",
  ];

  return (
    <React.Fragment>
      <YouTubeModal
        isOpen={youtubeModal.isOpen}
        toggle={() => setYoutubeModal({ isOpen: false, url: '', title: '' })}
        videoUrl={youtubeModal.url}
        title={youtubeModal.title}
      />
      {/* LA app install banner — never on white-label partner sites (it carries LA branding). */}
      {!__WHITE_LABEL_BUILD__ && !bannerExcludedPaths.some((path) => location.pathname.includes(path)) && <MobileAppBanner />}
      <SnackbarProvider maxSnack={2} />
      <NepalBranch />
      {/* <NotificationAlert ref={notificationAlertRef} /> */}
      {/* {!store.getState().is_portal && store.getState().is_main && (
        <ScrollToTop>
          <Routes>
            {front.length > 0 &&
              front.map((props) => (
                <Route
                  key={props.path}
                  path={props.path}
                  element={markReady(<props.component />)}
                  exact
                />
              ))}
            <Route path="/auth/*" element={<AuthLayout />}>
              <Route path="login" element={<Login />} />
              {getAuthRoutes()}
            </Route>

            {token != null && (
              <Route path="/admin/*" element={<AdminLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                {getAdminRoutes()}
              </Route>
            )}
            {token != null && (
              <Route path="/sub/subscription" element={<Sub />} />
            )}
            {token != null && (
              <Route path="/admin/sub/payment/form" element={<AdminLayout />} />
            )}
            {token != null && (
              <Route path="/admin/sub/success" element={<AdminLayout />} />
            )}
            {token === null && (
              <Route
                path="/auth/login/session-expired"
                element={<Navigate to="/auth/login/session-expired" replace />}
              />
            )}
            <Route path="*" element={markReady(<NotFound />)} exact />
          </Routes>
          <ChromePopUp />
          {isMobile && <DownloadApp os="test" />}
        </ScrollToTop>
      )} */}
      <ScrollToTop>
        <Suspense fallback={<LaLoader show={true} />}>
          <Routes>
            {front
              .filter((props) => props.component)
              .map((props) => (
                <Route
                  key={props.path}
                  path={props.path}
                  element={
                    <RouteErrorBoundary routeName={props.path}>
                      <RouteContentMarker>
                        <props.component />
                      </RouteContentMarker>
                    </RouteErrorBoundary>
                  }
                  exact
                />
              ))}

            <Route path="/auth/*" element={markReady(<AuthLayout />)}>
              <Route path="login" element={<LoginWithCaptcha />} />
              {getAuthRoutes()}
            </Route>

            <Route
              path="/admin/*"
              element={
                token ? <AdminLayout /> : <Navigate to="/auth/login" replace />
              }
            >
              {token && (
                <>
                  <Route index element={<Navigate to="dashboard" replace />} />
                  {getAdminRoutes()}
                </>
              )}
            </Route>

            {!__WHITE_LABEL_BUILD__ && (
              <Route
                path="/sub/subscription"
                element={token ? <Sub /> : <Navigate to="/auth/login" replace />}
              />
            )}

            {token != null && (
              <>
                <Route path="/sub/payment/form" element={<Sub />} />
                <Route path="/sub/success" element={<Sub />} />
              </>
            )}

            {token === null && (
              <>
                <Route
                  path="/auth/login/session-expired"
                  element={<Navigate to="/auth/login" replace />}
                />
              </>
            )}

            {/* White-label: redirect legacy /ptemax/* URLs to root paths */}
            {__WHITE_LABEL_BUILD__ && (
              <Route path="/ptemax/*" element={<PteMaxLegacyRedirect />} />
            )}

            <Route path="*" element={markReady(isWhiteLabel() ? <PteMaxNotFound/> : <NotFound />)} exact />
          </Routes>
        </Suspense>

        <ChromePopUp />
        {/* White-label partners don't get the LA app download prompt. */}
        {!__WHITE_LABEL_BUILD__ &&
          !location.pathname.startsWith("/admin") &&
          !location.pathname.startsWith("/sub") && <AppDownloadPopup />}
      </ScrollToTop>
    </React.Fragment>
  );
};

export default connect(mapStateToProps, null)(ReactRouteAll);
